/**
 * useCheckInNotifications Hook
 * Manages check-in reminder notifications
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import checkInNotificationService from '../services/checkInNotificationService';

export const useCheckInNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch unread check-in reminders
  const fetchUnreadReminders = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const { data, error } = await checkInNotificationService.getUnreadCheckInReminders(userId);
      
      if (error) throw error;
      
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching check-in reminders:', error);
      // Don't throw - just log and continue
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Subscribe to new check-in reminders in real-time
  useEffect(() => {
    if (!userId) return;

    try {
      // Fetch initial reminders
      fetchUnreadReminders();

      // Subscribe to new notifications using modern Supabase Realtime API
      const channel = supabase
        .channel(`notifications_${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            try {
              if (
                payload.new.type === 'check_in_reminder' &&
                !payload.new.is_read
              ) {
                // Add new notification to the list (at the beginning)
                setNotifications(prev => [payload.new, ...prev]);
              }
            } catch (err) {
              if (process.env.NODE_ENV === 'development') {
                console.error('Error handling notification insert:', err);
              }
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error setting up check-in notifications:', err);
      }
    }
  }, [userId, fetchUnreadReminders]);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const { error } = await checkInNotificationService.markCheckInReminderAsRead(
        notificationId
      );

      if (error) throw error;

      // Remove from displayed notifications
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Dismiss all notifications for a shift
  const dismissShiftReminders = useCallback(async (shiftId) => {
    const shiftNotifications = notifications.filter(n => n.data?.shift_id === shiftId);
    
    for (const notification of shiftNotifications) {
      await markAsRead(notification.id);
    }
  }, [notifications, markAsRead]);

  return {
    notifications,
    isLoading,
    markAsRead,
    dismissShiftReminders,
    refetch: fetchUnreadReminders
  };
};

export default useCheckInNotifications;
